/**
 * Debug logger for document highlighting issues in LexaLabs
 * 
 * This utility enhances console logging with additional information
 * about document highlighting to help diagnose issues.
 */

// Log level settings
enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

// Configuration
const DEBUG_CONFIG = {
  enabled: true,
  level: LogLevel.DEBUG,
  prefix: '🔍 [Highlight Debug]',
  logToConsole: true,
  logToStorage: true,
  storageKey: 'lexalabs_debug_logs'
}

// Store for debug logs
const debugLogs: string[] = [];
const MAX_LOGS = 1000;

/**
 * Main debug logger function
 */
export const debugLog = (level: LogLevel, message: string, data?: any) => {
  if (!DEBUG_CONFIG.enabled || level > DEBUG_CONFIG.level) return;

  const timestamp = new Date().toISOString();
  const levelStr = LogLevel[level];
  const formattedMsg = `${timestamp} - ${levelStr}: ${message}`;
  
  // Prepare the log entry
  let logEntry = formattedMsg;
  if (data) {
    // For object data, stringify but limit size
    const dataStr = typeof data === 'object' 
      ? JSON.stringify(data, (key, value) => {
          if (typeof value === 'string' && value.length > 500) {
            return value.substring(0, 500) + '...';
          }
          return value;
        }, 2)
      : String(data);
    logEntry += `\nData: ${dataStr}`;
  }
  
  // Console logging
  if (DEBUG_CONFIG.logToConsole) {
    switch (level) {
      case LogLevel.ERROR:
        console.error(`${DEBUG_CONFIG.prefix} ${logEntry}`);
        break;
      case LogLevel.WARN:
        console.warn(`${DEBUG_CONFIG.prefix} ${logEntry}`);
        break;
      case LogLevel.INFO:
        console.info(`${DEBUG_CONFIG.prefix} ${logEntry}`);
        break;
      case LogLevel.DEBUG:
        console.debug(`${DEBUG_CONFIG.prefix} ${logEntry}`);
        break;
    }
  }
  
  // Storage logging
  if (DEBUG_CONFIG.logToStorage) {
    debugLogs.push(logEntry);
    // Keep log size reasonable
    if (debugLogs.length > MAX_LOGS) {
      debugLogs.shift();
    }
    
    // Save to local storage (only if browser)
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(DEBUG_CONFIG.storageKey, JSON.stringify(debugLogs));
      } catch (e) {
        // Storage might be full, try to save a smaller version
        try {
          const truncatedLogs = debugLogs.slice(-100); // Keep only the last 100 logs
          localStorage.setItem(DEBUG_CONFIG.storageKey, JSON.stringify(truncatedLogs));
        } catch (e2) {
          // If still failing, just ignore storage
        }
      }
    }
  }
}

// Convenience methods
export const logError = (message: string, data?: any) => debugLog(LogLevel.ERROR, message, data);
export const logWarn = (message: string, data?: any) => debugLog(LogLevel.WARN, message, data);
export const logInfo = (message: string, data?: any) => debugLog(LogLevel.INFO, message, data);
export const logDebug = (message: string, data?: any) => debugLog(LogLevel.DEBUG, message, data);

/**
 * Export debug logs as a file
 */
export const exportDebugLogs = () => {
  if (typeof window === 'undefined') return null;
  
  const logsText = debugLogs.join('\n\n');
  const blob = new Blob([logsText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `lexalabs_debug_${new Date().toISOString().replace(/:/g, '-')}.log`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Clear debug logs
 */
export const clearDebugLogs = () => {
  debugLogs.length = 0;
  if (typeof window !== 'undefined') {
    localStorage.removeItem(DEBUG_CONFIG.storageKey);
  }
}

/**
 * Log document highlighting operation
 */
export const logHighlightOperation = (
  fileName: string, 
  htmlLength: number, 
  quotes: string[], 
  successCount: number
) => {
  // Basic highlighting info
  logInfo(`Document highlighting for: ${fileName}`, {
    documentSize: htmlLength,
    quotesCount: quotes.length,
    successCount
  });
  
  // Log each quote 
  quotes.forEach((quote, index) => {
    const truncatedQuote = quote.length > 100 ? `${quote.substring(0, 100)}...` : quote;
    logDebug(`Quote ${index}: ${truncatedQuote}`);
  });
}

/**
 * Enhanced error logging for highlights
 */
export const logHighlightError = (error: Error, fileName: string, quotes: string[]) => {
  logError(`Highlighting error for ${fileName}`, {
    error: error.message,
    stack: error.stack,
    quotesCount: quotes.length
  });
}

// Initialize by loading any existing logs from storage
if (typeof window !== 'undefined') {
  try {
    const storedLogs = localStorage.getItem(DEBUG_CONFIG.storageKey);
    if (storedLogs) {
      const parsedLogs = JSON.parse(storedLogs);
      if (Array.isArray(parsedLogs)) {
        debugLogs.push(...parsedLogs);
      }
    }
  } catch (e) {
    // Ignore storage errors during initialization
  }
}

const debugLogger = {
  logError,
  logWarn,
  logInfo,
  logDebug,
  logHighlightOperation,
  logHighlightError,
  exportDebugLogs,
  clearDebugLogs
};

export default debugLogger;